import { Toolbar } from '@/components/DesignSystem';
import { Filter, FilterSelections } from '@/components/DesignSystem/Toolbar/interfaces';
import { QueryKeys } from '@/constants/QueryKeys';
import { useSearch } from '@/hooks/useSearch';
import { getProgramPeriodLabel, sortPrograms } from '@/pages/RetailerOverview/helpers';
import {
  PurchaseCategoryFilterChip,
} from '@/pages/RetailerOverview/PointsOverview/PurchaseCategoryFilterChip';
import { RetailerApi } from '@/utilities/api/RetailerApi';
import { UserApi } from '@/utilities/api/UserApi';
import { RetailerEndpoint } from '@api/endpoints';
import { ApiRetailer } from '@api/interfaces';
import { UserType } from "@shared/enums";
import { getAccountName } from '@shared/utilities';
import React, { useEffect, useMemo, useRef } from 'react';
import { useInfiniteQuery, useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import { agronomyProgramsOnly } from '@/utilities/RewardsProgramUtility';

const defaultFilterIds = [
  'date-range',
  'origin',
  'wallet',
  'category',
  'location',
  'salesperson',
  'customer',
  'program-period',
];

interface PointsOverviewToolbarProps {
  filterIds?: string[],
  filterSelections?: FilterSelections,
  hasPurchasedCategory?: boolean,
  initialSelections?: FilterSelections,
  onChange: (
    body: RetailerEndpoint.GetPointsOverview.Request,
    selections: FilterSelections | undefined,
  ) => void,
  onChangePurchaseCategory?: (categoryIds: string[], hasPurchased: boolean | undefined) => void,
  purchaseCategoryIds?: string[],
  retailer: ApiRetailer,
  selectedProgramId?: string,
}

export const PointsOverviewToolbar = ({
  filterIds = defaultFilterIds,
  filterSelections,
  hasPurchasedCategory,
  initialSelections,
  onChange,
  onChangePurchaseCategory,
  purchaseCategoryIds = [],
  retailer,
  selectedProgramId,
}: PointsOverviewToolbarProps) => {
  const { t } = useTranslation();
  const {
    search: customerSearch,
    debouncedSearch: debouncedCustomerSearch,
    setSearch: setCustomerSearch,
  } = useSearch();

  const lastToolbarSelectionsRef = useRef<FilterSelections | undefined>(undefined);


  const sortedPrograms = sortPrograms(agronomyProgramsOnly(retailer?.rewardsPrograms));

  const buildRequest = (
    newSelections: FilterSelections | undefined,
    categoryIds: string[],
    hasPurchased: boolean | undefined,
  ): RetailerEndpoint.GetPointsOverview.Request => {
    const selectedDateRange = newSelections?.get('date-range')?.values().next().value;
    let period: 'day' | 'week' | 'month' | undefined = undefined;
    let periodCount: number | undefined = undefined;
    if (selectedDateRange) {
      const parts = selectedDateRange.split('-');
      period = parts[0] as 'day' | 'week' | 'month';
      periodCount = Number(parts[1]);
    }
    const selectedWallets = Array.from(newSelections?.get('wallet') ?? []);
    const selectedOrigin = newSelections?.get('origin')?.values().next().value;
    const selectedCategories = Array.from(newSelections?.get('category') ?? []);
    const selectedLocations = Array.from(newSelections?.get('location') ?? []);
    const selectedSalespersons = Array.from(newSelections?.get('salesperson') ?? [])
      .filter((id) => !id.startsWith('$location'));
    const selectedCustomers = Array.from(newSelections?.get('customer') ?? []);
    const selectedProgramPeriod = newSelections?.get('program-period')?.values().next().value;
    return {
      ...(period ? { period } : {}),
      ...(periodCount ? { periodCount } : {}),
      ...(selectedOrigin ? { origin: selectedOrigin } : {}),
      ...(selectedCategories.length ? { categoryId: selectedCategories } : {}),
      ...(selectedLocations.length ? { locationId: selectedLocations } : {}),
      ...(selectedSalespersons.length ? { salespersonId: selectedSalespersons } : {}),
      ...(selectedCustomers.length ? { customerId: selectedCustomers } : {}),
      ...(selectedProgramPeriod ? { programId: selectedProgramPeriod } : {}),
      ...(selectedWallets.length ? { subwalletId: selectedWallets } : {}),
      ...(hasPurchased === true ? { hasPurchasedCategoryId: categoryIds } : {}),
      ...(hasPurchased === false ? { hasNotPurchasedCategoryId: categoryIds } : {}),
    } as RetailerEndpoint.GetPointsOverview.Request;
  };

  const onChangeFilter = (newSelections?: FilterSelections) => {
    lastToolbarSelectionsRef.current = newSelections;
    onChange(
      buildRequest(newSelections, purchaseCategoryIds, hasPurchasedCategory),
      newSelections,
    );
  };

  const handlePurchaseCategoryChange = (
    categoryIds: string[],
    hasPurchased: boolean | undefined,
  ) => {
    onChangePurchaseCategory?.(categoryIds, hasPurchased);
    onChange(
      buildRequest(lastToolbarSelectionsRef.current, categoryIds, hasPurchased),
      lastToolbarSelectionsRef.current,
    );
  };

  useEffect(() => {
    if (purchaseCategoryIds.length) {
      onChangePurchaseCategory?.([], undefined);
      onChange(
        buildRequest(lastToolbarSelectionsRef.current, [], undefined),
        lastToolbarSelectionsRef.current,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProgramId]);

  const { data: filterOptions } = useQuery(
    [QueryKeys.GET_POINTS_OVERVIEW_FILTER_OPTIONS, retailer.id, selectedProgramId],
    () => RetailerApi.getPointsOverviewFilterOptions(
      retailer.id,
      selectedProgramId ? { programId: selectedProgramId } : undefined,
    ),
  );

  const {
    data: customers,
    fetchNextPage,
    hasNextPage,
    isFetching: areCustomersFetching,
  } = useInfiniteQuery(
    [QueryKeys.LIST_USERS, retailer.id, debouncedCustomerSearch],
    async ({ pageParam = 0 }) => (
      UserApi.list(
        {
          limit: 20,
          page: pageParam,
          retailerId: retailer.id,
          search: debouncedCustomerSearch,
          userType: [UserType.Farmer],
        },
      )
    ),
    {
      enabled: filterIds.includes('customer'),
      getNextPageParam: (lastPage) =>
        lastPage.page < lastPage.lastPage ? lastPage.page + 1 : undefined,
      keepPreviousData: true,
    },
  );

  const filters: Filter[] = useMemo(
    () => (
      filterIds.map((id) => ([
        {
          id: 'date-range',
          isChipLabelVerbose: true,
          label: t($ => $.overview.filters.dateRange),
          options: [
            {
              id: 'day-7',
              label: retailer.isProgramCreationDisabled 
                ? t($ => $.overview.filters.dateRangeOptions.lastDays, { count: 7 })
                : t($ => $.overview.filters.dateRangeOptions.lastDaysWithAsterisk, { count: 7 }),
            },
            {
              id: 'day-14',
              label: retailer.isProgramCreationDisabled 
                ? t($ => $.overview.filters.dateRangeOptions.lastDays, { count: 14 })
                : t($ => $.overview.filters.dateRangeOptions.lastDaysWithAsterisk, { count: 14 }),
            },
            {
              id: 'week-4',
              label: retailer.isProgramCreationDisabled 
                ? t($ => $.overview.filters.dateRangeOptions.lastWeeks, { count: 4 })
                : t($ => $.overview.filters.dateRangeOptions.lastWeeksWithAsterisk, { count: 4 }),
            },
            {
              id: 'month-3',
              label: retailer.isProgramCreationDisabled 
                ? t($ => $.overview.filters.dateRangeOptions.lastMonths, { count: 3 })
                : t($ => $.overview.filters.dateRangeOptions.lastMonthsWithAsterisk, { count: 3 }),
            },
            {
              id: 'month-6',
              label: retailer.isProgramCreationDisabled 
                ? t($ => $.overview.filters.dateRangeOptions.lastMonths, { count: 6 })
                : t($ => $.overview.filters.dateRangeOptions.lastMonthsWithAsterisk, { count: 6 }),
            },
            {
              id: 'month-9',
              label: retailer.isProgramCreationDisabled 
                ? t($ => $.overview.filters.dateRangeOptions.lastMonths, { count: 9 })
                : t($ => $.overview.filters.dateRangeOptions.lastMonthsWithAsterisk, { count: 9 }),
            },
          ],
          selectionMethod: 'single-select',
          truncateLabel: false,
        },
        {
          id: 'origin',
          isChipLabelVerbose: true,
          label: t($ => $.overview.filters.pointsSource),
          options: [
            {
              id: 'program',
              label: t($ => $.overview.filters.originOptions.program),
            },
            {
              id: 'promotion',
              label: t($ => $.overview.filters.originOptions.promotion),
            },
          ],
          selectionMethod: 'single-select',
        },
        {
          id: 'wallet',
          isChipLabelVerbose: true,
          label: t($ => $.overview.filters.wallet),
          options: filterOptions?.subwallets ?? [],
          selectionMethod: 'multi-select',
        },
        {
          id: 'category',
          isChipLabelVerbose: true,
          label: t($ => $.overview.filters.category),
          options: filterOptions?.categories ?? [],
          selectionMethod: 'multi-select',
        },
        {
          id: 'location',
          isChipLabelVerbose: true,
          label: t($ => $.overview.filters.location),
          options: filterOptions?.locations ?? [],
          selectionMethod: 'multi-select',
        },
        {
          id: 'salesperson',
          isChipLabelVerbose: true,
          label: t($ => $.overview.filters.salesperson),
          options: filterOptions?.salespersons ?? [],
          selectionMethod: 'multi-select',
        },
        {
          id: 'customer',
          isChipLabelVerbose: true,
          label: t($ => $.common.customer),
          infiniteScrollOptions: {
            fetchMoreItems: fetchNextPage,
            hasMore: hasNextPage ?? true,
            isLoading: areCustomersFetching,
          },
          onSearch: (search) => setCustomerSearch(search),
          options: (customers?.pages?.flatMap(({ data }) => data) ?? []).map((customer) => ({
            id: customer.id,
            label: getAccountName(customer),
          })) ?? [],
          search: customerSearch,
          selectionMethod: 'multi-select',
        },
        {
          hideClearButton: true,
          id: 'program-period',
          isChipLabelVerbose: true,
          label: t($ => $.overview.filters.programPeriod),
          options: sortedPrograms.map((program) => ({
            id: program.id,
            label: getProgramPeriodLabel(program),
          })),
          selectionMethod: 'single-select',
          truncateLabel: false,
        },
      ] as Filter[]).find((filter) => filter.id === id))
        .filter((filter): filter is Filter => !!filter)
    ), [
      areCustomersFetching,
      customers,
      customerSearch,
      fetchNextPage,
      filterIds,
      filterOptions,
      hasNextPage,
      retailer,
      setCustomerSearch,
      t,
    ],
  );

  return (
    <>
      <Toolbar
        filterSelections={filterSelections}
        filters={filters}
        hideSearch
        initialSelections={initialSelections}
        onChange={({ selections }) => {
          onChangeFilter(selections);
        }}
        testID="points-overview-filters"
      />
      {filterIds.includes('purchase-category') && (
        <PurchaseCategoryFilterChip
          hasPurchasedCategory={hasPurchasedCategory}
          onChange={handlePurchaseCategoryChange}
          options={filterOptions?.purchaseCategories ?? []}
          purchaseCategoryIds={purchaseCategoryIds}
          testID="purchase-category-filter-chip"
        />
      )}
    </>
  );
};

